import axios from 'axios';
import { isCollectionPass, normalizeIdrCode } from '@/utils/idr-code';
import { REDEEM_MESSAGES, redeemErrorMessage } from '@/utils/redeem-messages';
import { buildScannerDisplayMessage } from '@/utils/scanner-display-message';
import { hasLinkedCg } from '@/utils/cg-status';
import { BMT_CHECK_IN_PATH, BMT_SCAN_TYPE } from '@/constants/bmt';

const SCANNER_API_PATH = '/scanner';

const getRouter = contextOrRouter => contextOrRouter?.router || contextOrRouter;
const getStore = contextOrRouter => contextOrRouter?.store;

const normalizeScannerPayload = data => {
  if (data?.type === 'scanner' && data.scanner) return data.scanner;
  if (data?.type && data?.action && data?.ref) {
    const { type, action, ref, query = {} } = data;
    return { type, action, ref, query };
  }
  return null;
};

/**
 * The opaque token a server-handled QR carries: the last segment of its path,
 * https://hogc.one/scan/{type}/{action}/{token}. The API's ScannerDto requires
 * `{ type, action, token }` (KD29).
 *
 * Sent for BMT scans only, which sit behind `bmt-companion`. Sending it for every
 * type would also bring back the milestones, board-game and photo-drop lanes
 * outside any flag; that is a separate change.
 */
export const scannerTokenFromRef = ref => {
  const segments = String(ref || '')
    .split('/')
    .filter(Boolean);
  return segments.length ? segments[segments.length - 1] : '';
};

export const buildScannerRequestBody = payload =>
  payload.type === BMT_SCAN_TYPE
    ? { ...payload, token: scannerTokenFromRef(payload.ref) }
    : payload;

/**
 * A BMT scan changes what /me says (attended, check-in state), so re-read it
 * whatever the outcome — even "not open yet" can mean the window just moved.
 * Not awaited: the scan result is shown without waiting for the refresh.
 */
const refreshBmtAfterScan = store => {
  if (typeof store?.dispatch !== 'function') return;
  Promise.resolve(store.dispatch('bmt/fetchMe')).catch(() => {});
};

/**
 * The part of a POST /scanner answer the /bmt/check-in screen reads. A network
 * failure (no body) becomes a plain error, which that screen shows as "That
 * didn't work".
 */
export const bmtCheckInResult = response => ({
  status: response?.status === 'success' ? 'success' : 'error',
  code: response?.data?.code || null,
  display: response?.display || null,
  checkIn: response?.data?.checkIn || null,
  opensAt: response?.data?.opensAt || null,
});

const getAccessToken = store => {
  const storeToken = store?.state?.user?.token;
  if (storeToken) return storeToken;
  if (typeof localStorage === 'undefined') return '';
  return localStorage.getItem('token') || '';
};

// Course-unlock QR codes (type: 'course', action: 'unlock') — e.g. the Lesson
// 3&4 class QR: https://hogc.one/scan/course/unlock/next-steps-p1?lessons=3,4.
// The grant is server-authoritative: we call the courses store's classUnlock
// action (POST /api/courses/:slug/class-unlock), which grants the TRIAL tier
// and refreshes the access summary so the carousel + reader reflect the unlock.
// The `lessons` param is only the navigation target (which lesson to land on);
// the raw param can never self-grant. Idempotent: an already-granted unlock
// returns success from the server, so a repeat scan just re-navigates — no
// "already unlocked" error, no duplicate grant.
/**
 * Whether the scanning member still needs to link a Connect Group.
 *
 * Fails OPEN: if the profile can't be resolved, the unlock proceeds. Unlike the
 * courses route guard — which fails closed because it protects content access —
 * this gate protects a data field, so a network blip must not block someone
 * standing in class with a valid QR code.
 */
async function needsCgLink(store) {
  try {
    await store.dispatch('profile/fetchProfile');
  } catch (error) {
    console.warn('[Scanner] Could not resolve profile for CG check:', error?.message);
    return false;
  }
  const cgName = store.getters?.['profile/cgName'];
  return !hasLinkedCg(cgName);
}

/**
 * Leave the scan lane by REPLACING the current history entry, not pushing onto it.
 *
 * `/scan/course/unlock/...` redirects to `/scanner?...&token=...`, and the scanner
 * page dispatches the unlock from `ionViewWillEnter`. A push leaves that /scanner
 * entry in history WITH THE TOKEN STILL IN ITS QUERY, and three things follow, in
 * order of how bad they are:
 *
 *   1. Back re-enters /scanner, whose ionViewWillEnter reads the query again and
 *      RE-DISPATCHES the grant. A back gesture must never re-POST anything.
 *   2. It then navigates forward again mid-back-transition, leaving Ionic's view
 *      stack inconsistent — the reported white page.
 *   3. The member sees the in-app scanner flash past, a surface they never asked
 *      for and which is not a released feature for them.
 *
 * Replacing removes the entry. /scanner was an artifact of the redirect, never a
 * page the member chose to visit, so it does not belong in their history.
 *
 * Falls back to `push` when a caller supplied only that — old behaviour rather
 * than a crash.
 */
async function leaveScanLane(router, path) {
  if (router?.replace) return router.replace(path);
  if (router?.push) return router.push(path);
  return undefined;
}

async function handleCourseUnlock(payload, store, router) {
  const programSlug = payload.ref || '';
  const lessons = String(payload.query?.lessons || '')
    .split(',')
    .map(n => parseInt(n, 10))
    .filter(n => !Number.isNaN(n));
  // The class QR names which lessons to land on, so it is required again. The
  // leader unlock QR no longer arrives here at all — it has its own action
  // (`course/leader-unlock`) and its own page (pages/courses/unlock.vue), because
  // routing a camera scan through this one showed members a scanner they never
  // asked for. See the router's /scan redirect.
  if (!store || !programSlug || !lessons.length) {
    return { success: false, message: 'Invalid HOGC scanner code.' };
  }

  // A member with no Connect Group on record must link one before the grant
  // runs. Zone, service and demography all resolve through the CG relation
  // server-side, so an unlock granted without a CG produces a member the
  // church can't place — and the QR scan is the one moment they're reachable.
  // Returning `needsCgLink` hands the prompt to the caller (the scanner page
  // owns UI); it re-runs this same payload once the CG is linked, which is
  // safe because classUnlock is idempotent.
  if (await needsCgLink(store)) {
    return { success: false, needsCgLink: true, payload, message: 'Connect Group required.' };
  }

  let response;
  try {
    // Pass through an opaque code if the QR carries one; the server authorizes
    // and grants (the client cannot unlock from the raw lessons param).
    response = await store.dispatch('courses/classUnlock', {
      programSlug,
      code: payload.query?.code,
    });
  } catch (error) {
    console.error('Course unlock failed:', error);
    return { success: false, message: 'Could not unlock this lesson. Please try again.' };
  }

  // Land on the (first) unlocked lesson whether this scan just granted it or it
  // was already unlocked — access has been refreshed by classUnlock above.
  let lessonsList = store.state.courses?.lessons || [];
  if (!lessonsList.length) {
    await store.dispatch('courses/fetchLessons');
    lessonsList = store.state.courses?.lessons || [];
  }
  const targetNumber = lessons[0];
  const targetLesson = lessonsList.find(
    l =>
      (l.programSlug || programSlug) === programSlug &&
      store.getters['courses/lessonNumber'](l) === targetNumber,
  );

  if (targetLesson) {
    await leaveScanLane(router, `/courses/${programSlug}/${targetLesson.slug}`);
  }

  return {
    success: true,
    message: 'Lesson unlocked!',
    response: { status: 'success', ...(response || {}) },
  };
}

// IDR unlock-card QRs (type: 'course', action: 'redeem') — the card printed with
// an NS1/NS2 book bought at the IDR counter, e.g.
// https://hogc.one/scan/course/redeem/IDRU-EK1E-A0Y8-AWAD-K3NT.
//
// Server-authoritative, like the class-unlock lane above: we hand the code to
// hogc-one-api, which asks IDR whether the card is good, grants FULL and spends
// the card. The app never talks to IDR and never learns which program a card is
// for until the API says so — the program comes from IDR's contentKey, not from
// anything on this URL.
//
// Ungated, and deliberately not behind the `scanner` flag: these cards are
// scanned with the phone's own camera, so the lane has to work with the in-app
// scanner switched off.
//
// Idempotent: re-scanning a card this member already redeemed returns the same
// slug and just re-navigates. No "already unlocked" toast — see the plan's
// error matrix.
async function handleCourseRedeem(payload, store, router) {
  const raw = payload.ref || '';
  const code = normalizeIdrCode(raw);
  if (!code) {
    return {
      success: false,
      message: isCollectionPass(raw)
        ? REDEEM_MESSAGES.COLLECTION_PASS
        : REDEEM_MESSAGES.INVALID_CODE,
    };
  }
  if (!store) {
    return { success: false, message: REDEEM_MESSAGES.INVALID_CODE };
  }

  let result;
  try {
    result = await store.dispatch('courses/redeemBookCode', { code });
  } catch (error) {
    console.error('Book code redemption failed:', error);
    return { success: false, message: redeemErrorMessage(error) };
  }

  // The server can still refuse to grant until this member has a main CG. There
  // is no program slug to navigate to in that answer, so send them to the Next
  // Steps chooser, which raises the CG prompt for a held code.
  if (result?.needsCgLink) {
    // Hand the card over before navigating, so the chooser raises the CG block
    // and finishes this redemption itself. Without this the member would be
    // told to link a CG and then have to find the card and retype the code.
    store.commit('courses/setCgPendingRedeemCode', code);
    if (router?.push) await router.push('/courses');
    return { success: false, message: REDEEM_MESSAGES.NEEDS_CG_LINK };
  }

  if (result?.error) {
    return { success: false, message: result.error };
  }

  const programSlug = result?.unlockedProgramSlug;
  if (!programSlug) {
    return { success: false, message: REDEEM_MESSAGES.DEFAULT };
  }

  // Always land on the program the card unlocked — never only the chooser.
  // fetchCourseAccess has already been refreshed by the store action, so the
  // entitlement guard on this route will see the new grant.
  if (router?.push) {
    await router.push(`/courses/${programSlug}`);
  }

  // `replay` means the server matched this member's own earlier redemption and
  // spent nothing. They still get their course — but saying "Course unlocked!"
  // would claim a card burned that did not, and a member testing a card cannot
  // tell the two apart.
  return {
    success: true,
    message: result?.replay
      ? REDEEM_MESSAGES.CODE_ALREADY_REDEEMED
      : REDEEM_MESSAGES.COURSE_UNLOCKED,
    response: { status: 'success', ...(result || {}) },
  };
}

/**
 * BMT check-in. The outcome goes into the bmt store and the trainee lands on
 * /bmt/check-in, a full screen for each answer, instead of a toast. The scanner
 * entry is replaced, like the course lanes, so Back never re-posts a scan.
 */
async function handleBmtCheckIn(payload, store, router) {
  let response = null;
  try {
    ({ data: response } = await axios.post(
      `${process.env.VUE_APP_API_URL}${SCANNER_API_PATH}`,
      buildScannerRequestBody(payload),
      { headers: { Authorization: `Bearer ${getAccessToken(store)}` } },
    ));
  } catch (error) {
    // An HTTP error may still carry the API's envelope; a network failure has none.
    response = error?.response?.data || null;
  } finally {
    refreshBmtAfterScan(store);
  }

  if (typeof store?.commit === 'function') {
    store.commit('bmt/SET_LAST_CHECK_IN', bmtCheckInResult(response));
  }
  await leaveScanLane(router, BMT_CHECK_IN_PATH);

  return {
    success: response?.status === 'success',
    // The result screen says everything; the scanner must not toast as well.
    navigated: true,
    message: buildScannerDisplayMessage(response, 'Scan processed.', {
      includeCode: response?.status !== 'success',
    }),
    response,
  };
}

/**
 * General-purpose QR code dispatcher.
 *
 * Scanner-platform QRs use the backend URL contract:
 *   https://hogc.one/scan/{type}/{action}/{ref}?q=...
 *
 * Legacy feature-specific QR codes can still register local handlers.
 */

const handlers = {
  scanner: async (data, contextOrRouter) => {
    const payload = normalizeScannerPayload(data);
    if (!payload) {
      return { success: false, message: 'Invalid HOGC scanner code.' };
    }

    const store = getStore(contextOrRouter);
    const router = getRouter(contextOrRouter);

    if (payload.type === 'course' && payload.action === 'unlock') {
      return handleCourseUnlock(payload, store, router);
    }

    if (payload.type === 'course' && payload.action === 'redeem') {
      return handleCourseRedeem(payload, store, router);
    }

    if (payload.type === BMT_SCAN_TYPE) {
      return handleBmtCheckIn(payload, store, router);
    }

    const token = getAccessToken(store);
    const { data: response } = await axios.post(
      `${process.env.VUE_APP_API_URL}${SCANNER_API_PATH}`,
      buildScannerRequestBody(payload),
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );

    const display = response?.display;
    if (display?.type === 'redirect' && display.redirectTo && router?.push) {
      await router.push(display.redirectTo);
    }

    return {
      success: response?.status === 'success',
      message: buildScannerDisplayMessage(response, 'Scan processed.', {
        includeCode: response?.status !== 'success',
      }),
      response,
    };
  },
  voucher: (data, router) => {
    if (!data.voucherId) {
      return { success: false, message: 'Invalid voucher QR code.' };
    }
    getRouter(router)?.push(`/vouchers/${data.voucherId}`);
    return { success: true, message: 'Opening voucher...' };
  },
  // Cross-device SSO: the phone scans the QR shown on a sister app's desktop
  // login screen and opens the approval page in-app, with the pairing code
  // prefilled from the link. This is a local navigation, not a backend call —
  // the member approves on the page, and the broker verifies the code there.
  'sso-approval': (data, contextOrRouter) => {
    const router = getRouter(contextOrRouter);
    const { requestId, code } = data.ssoApproval || {};
    if (!requestId || !router?.push) {
      return { success: false, message: 'Invalid login QR code.' };
    }
    router.push({
      path: `/sso/approve/${requestId}`,
      query: code ? { code } : {},
    });
    return { success: true, message: 'Opening login approval...' };
  },
};

/**
 * Dispatches a parsed QR result to the appropriate handler.
 * @param {Object} data - Parsed QR data with a `type` field
 * @param {Object} router - Vue Router instance
 * @returns {{ success: boolean, message: string }|Promise<{ success: boolean, message: string }>}
 */
export function handleQrResult(data, router) {
  if (!data || !data.type) {
    return { success: false, message: 'Invalid QR code format.' };
  }

  if (data.type === 'unknown') {
    return { success: false, message: "That QR code isn't a HOGC scanner code." };
  }

  const scannerPayload = normalizeScannerPayload(data);
  if (scannerPayload) {
    return handlers.scanner({ type: 'scanner', scanner: scannerPayload }, router);
  }

  const handler = handlers[data.type];
  if (!handler) {
    return { success: false, message: `Unsupported QR code type: "${data.type}".` };
  }

  return handler(data, router);
}

/**
 * Register a new QR type handler at runtime.
 * @param {string} type - QR type identifier
 * @param {Function} handler - (data, router) => { success, message }
 */
export function registerQrHandler(type, handler) {
  handlers[type] = handler;
}
